import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { responsiveFontSizes, styled } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';
import type {} from '@mui/material/themeCssVarsAugmentation';
import Typography from '@mui/material/Typography';

import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import MobiledataOffIcon from '@mui/icons-material/MobiledataOff';
import SignalWifi4BarIcon from '@mui/icons-material/SignalWifi4Bar';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import GratiAppBar from './components/common/GratiAppBar';
import GratiNavRail from './components/common/GratiNavRail';
import GratiNoticeBanner from './components/common/GratiNoticeBanner';
import Hourglass from './components/common/Hourglass';
import { useApp } from './contextProviders/AppProvider';
import { useData } from './contextProviders/DataProvider';
import RouteTable from './routes/RouteTable';
import mui6Theme from './styles/mui6Theme';
import { useOnlineStatus } from './utils/useOnlineStatus';

function App() {
  const navRailWidth = '5rem';
  const { isEmulator, isOnline } = useOnlineStatus();
  const { isHomePage } = useApp();
  const { userState } = useData();

  dayjs.extend(utc);
  dayjs.extend(timezone);

  const responsiveMui6Theme = responsiveFontSizes(mui6Theme);
  const Offset = styled('div')(({ theme }) => theme.mixins.toolbar);

  // App pre-render until we are in a definitive state of logged in or logged out.
  if (!['ready', 'logged_out'].includes(userState.status)) {
    return (
      <ThemeProvider theme={responsiveMui6Theme} defaultMode="system">
        <Box sx={{ display: 'flex', flexGrow: 1 }}>
          <GratiAppBar
            isTopLevelPage={!(userState.status === 'ready') || isHomePage}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
          />
          <Stack
            className="AppStack"
            sx={{
              position: 'sticky',
              display: 'flex',
              flexGrow: 1,
              spacing: 1,
              justifyContent: 'center',
              alignItems: 'center',
              width: { xs: '100vw', md: `calc(100vw - ${navRailWidth})` },
              backgroundColor: 'surface.main',
            }}
          >
            <Offset />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Hourglass />
              <Typography variant="bodyLarge" color="text.secondary">
                {userState.status === 'authenticating_user' && 'Driving Range...'}
                {userState.status === 'authenticating_profile' && 'Pitching and Chipping...'}
                {userState.status === 'authenticating_memberships' && 'Putting...'}
                {userState.status === 'unknown' && 'Stretching...'}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={responsiveMui6Theme} defaultMode="system">
      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        <GratiAppBar
          isTopLevelPage={isHomePage}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        />
        {userState.status === 'ready' && <GratiNavRail sx={{ display: { xs: 'none', md: 'flex' } }} />}
        <Stack
          className="AppStack"
          sx={{
            position: 'sticky',
            display: 'flex',
            flexGrow: 1,
            spacing: 1,
            justifyContent: 'flex-start',
            width: { xs: '100vw', md: `calc(100vw - ${navRailWidth})` },
            backgroundColor: 'surface.main',
          }}
        >
          <Offset />
          <Stack  sx={{ p: 1 }}>
            {userState.status === 'ready' && isHomePage && (
              <GratiNoticeBanner
                sx={{
                  height: '4rem',
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'primary.container',
                }}
              />
            )}
            <RouteTable />
            {(!isOnline || isEmulator) && (
              <Stack
                direction="row"
                spacing={1}
                divider={<Divider orientation="vertical" flexItem />}
                sx={{
                  display: 'flex',
                  flexGrow: 1,
                  px: 2,
                  backgroundColor: 'surface.main',
                  color: 'surface.contrastText',
                }}
              >
                {isOnline ? (
                  <SignalWifi4BarIcon sx={{ color: 'success.main' }} />
                ) : (
                  <MobiledataOffIcon sx={{ color: 'error.main' }} />
                )}
                <Typography variant="labelMedium">
                  {isOnline ? 'Online' : 'Offline'}
                </Typography>
                {isEmulator ? (
                  <DeveloperModeIcon sx={{ color: 'success.main' }} />
                ) : (
                  <ImportExportIcon sx={{ color: 'warning.main' }} />
                )}
                <Typography variant="labelMedium" component="span">
                  {isEmulator ? 'Emulator' : 'Production'}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Box>
    </ThemeProvider>
  );
}

export default App;
